import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';
import LandingCtaButton from './LandingCtaButton';
import SalovareWordmark from '../common/brand/SalovareWordmark';
import { LANDING_COLORS } from './landingTokens';
import { useAuth } from '../../context/AuthContext';
import useLocalizedMarketingPath from '../../hooks/useLocalizedMarketingPath';

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const navLinkSx = {
  color: LANDING_COLORS.charcoal,
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '0.95rem',
  '&:hover': {
    color: LANDING_COLORS.terracotta,
    background: 'transparent',
  },
};

const languageSwitcherSx = {
  '& .MuiOutlinedInput-root': {
    color: LANDING_COLORS.charcoal,
    '& fieldset': { borderColor: LANDING_COLORS.line },
    '&:hover fieldset': { borderColor: LANDING_COLORS.terracotta },
  },
  '& .MuiSelect-icon': { color: LANDING_COLORS.charcoal },
};

const LandingHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isManagementAuthenticated } = useAuth();
  const localizePath = useLocalizedMarketingPath();
  const registrationPath = localizePath('/register-store');

  const goToControlPanel = () => {
    setMobileOpen(false);
    navigate('/management');
  };

  const navItems = [
    { id: 'features', label: t('landing.nav.features') },
    { id: 'how-it-works', label: t('landing.nav.howItWorks') },
    { id: 'pricing', label: t('landing.nav.pricing') },
    { id: 'faq', label: t('landing.nav.faq') },
  ];

  const handleNavClick = (sectionId) => {
    setMobileOpen(false);
    scrollToSection(sectionId);
  };

  const drawer = (
    <Box sx={{ width: 260, pt: 2, px: 1 }}>
      <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'center' }}>
        <SalovareWordmark title={t('brand')} sx={{ height: 34 }} />
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton onClick={() => handleNavClick(item.id)}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        {!isManagementAuthenticated && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setMobileOpen(false); navigate('/management/team-member/login'); }}>
              <ListItemText primary={t('landing.nav.signIn')} />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      <Box sx={{ px: 2, pt: 2 }}>
        <LanguageSwitcher sx={{ width: '100%', mb: 2, ...languageSwitcherSx }} />
        <LandingCtaButton
          fullWidth
          onClick={isManagementAuthenticated
            ? goToControlPanel
            : () => { setMobileOpen(false); navigate(registrationPath); }}
        >
          {isManagementAuthenticated ? t('landing.nav.goToControlPanel') : t('landing.nav.getStarted')}
        </LandingCtaButton>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'rgba(245, 242, 238, 0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${LANDING_COLORS.line}`,
          boxShadow: 'none',
          color: LANDING_COLORS.charcoal,
        }}
      >
        <Container maxWidth="lg" sx={{ mx: 'auto' }}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 }, gap: 2, width: '100%' }}>
            <Box
              component={RouterLink}
              to={localizePath('/')}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label={t('brand')}
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                p: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <SalovareWordmark title={t('brand')} sx={{ height: { xs: 26, md: 30 } }} />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {navItems.map((item) => (
                  <Button key={item.id} onClick={() => handleNavClick(item.id)} sx={navLinkSx}>
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {!isMobile && (
              <>
                <LanguageSwitcher variant="compact" sx={{ minWidth: 100, mb: 0, ...languageSwitcherSx }} />
                {isManagementAuthenticated ? (
                  <LandingCtaButton
                    onClick={goToControlPanel}
                    sx={{ px: 2.5, py: 1, whiteSpace: 'nowrap' }}
                  >
                    {t('landing.nav.goToControlPanel')}
                  </LandingCtaButton>
                ) : (
                  <>
                    <Button
                      onClick={() => navigate('/management/team-member/login')}
                      sx={navLinkSx}
                    >
                      {t('landing.nav.signIn')}
                    </Button>
                    <LandingCtaButton
                      component={RouterLink}
                      to={registrationPath}
                      sx={{ px: 2.5, py: 1, whiteSpace: 'nowrap' }}
                    >
                      {t('landing.nav.getStarted')}
                    </LandingCtaButton>
                  </>
                )}
              </>
            )}

            {isMobile && (
              <IconButton
                edge="end"
                onClick={() => setMobileOpen(true)}
                aria-label={t('landing.nav.openMenu')}
                sx={{ color: LANDING_COLORS.navy }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ disableScrollLock: true }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default LandingHeader;
